/**
 * A rule's Alert Actions: which notifications it sends, to whom, and who sends them.
 *
 *   Dashboard     live alert on the boards (default on)
 *   Email         + recipients
 *   Message API   + numbers / WhatsApp groups
 *   Sent by       "This site" (the site's own Alert Actions channels) or
 *                 "Via Cloud" (the cloud's Central Alert Actions channels)
 *
 * Same component in the Alert Rules form (both apps) and the Alert Actions rule editor, so
 * a rule reads the same wherever it is edited.
 */
import {
  BellAlertIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  BuildingOffice2Icon,
  CloudIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, ReactNode } from 'react';
import { Callout } from '../common/Callout';
import { AASegmented, aaInputCls } from './AlertActionsControls';
import { isDashboardOn, viaLabel, type NotifyVia, type RuleNotificationValue } from './alertActionsModel';

export interface RuleNotificationFieldsProps {
  value: RuleNotificationValue;
  onChange: (patch: Partial<RuleNotificationValue>) => void;
  /** 'site' on a local node; 'central' when the cloud edits a site's rule. */
  perspective?: 'site' | 'central';
  /** Whether the site can relay to a cloud. null = unknown (don't warn). */
  cloudAvailable?: boolean | null;
  /** Why it can't, shown when "Via Cloud" is picked anyway. */
  cloudReason?: string | null;
  disabled?: boolean;
}

function Row({
  icon: Icon,
  label,
  hint,
  checked,
  onToggle,
  disabled,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  hint: string;
  checked: boolean;
  onToggle: (v: boolean) => void;
  disabled?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className={`rounded-lg border p-2.5 ${checked ? 'border-brand bg-card' : 'border-border bg-card'}`}>
      <label className="flex cursor-pointer items-start gap-2 text-[13px] text-foreground">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onToggle(e.target.checked)}
          className="mt-0.5 accent-brand"
        />
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${checked ? 'text-brand' : 'text-muted-foreground'}`} />
        <span className="min-w-0">
          <span className="font-medium">{label}</span>
          <span className="block text-[11px] text-muted-foreground">{hint}</span>
        </span>
      </label>
      {children && <div className={checked ? 'mt-2' : 'mt-2 opacity-50'}>{children}</div>}
    </div>
  );
}

export default function RuleNotificationFields({
  value,
  onChange,
  perspective = 'site',
  cloudAvailable = null,
  cloudReason = null,
  disabled,
}: RuleNotificationFieldsProps) {
  const via: NotifyVia = value.notify_via === 'cloud' ? 'cloud' : 'local';
  const sendsOut = !!value.email_enabled || !!value.message_api_enabled;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Row
          icon={BellAlertIcon}
          label="Dashboard"
          hint="Live alert on the alert boards"
          checked={isDashboardOn(value)}
          onToggle={(v) => onChange({ websocket_enabled: v })}
          disabled={disabled}
        />
        <Row
          icon={EnvelopeIcon}
          label="Email"
          hint="Through Email (SMTP)"
          checked={!!value.email_enabled}
          onToggle={(v) => onChange({ email_enabled: v })}
          disabled={disabled}
        >
          <textarea
            rows={2}
            value={value.email_recipients || ''}
            onChange={(e) => onChange({ email_recipients: e.target.value })}
            disabled={disabled || !value.email_enabled}
            className={`${aaInputCls} mt-0 resize-y`}
            placeholder="a@company.com, b@company.com"
          />
        </Row>
        <Row
          icon={ChatBubbleLeftRightIcon}
          label="Message API"
          hint="WhatsApp via Message API Server"
          checked={!!value.message_api_enabled}
          onToggle={(v) => onChange({ message_api_enabled: v })}
          disabled={disabled}
        >
          <textarea
            rows={2}
            value={value.message_api_recipients || ''}
            onChange={(e) => onChange({ message_api_recipients: e.target.value })}
            disabled={disabled || !value.message_api_enabled}
            className={`${aaInputCls} mt-0 resize-y`}
            placeholder="919876543210, 120363…@g.us"
          />
        </Row>
      </div>

      <div className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-border px-3 py-2 ${sendsOut ? '' : 'opacity-60'}`}>
        <span className="text-[12px] font-medium text-foreground">Email &amp; message sent by</span>
        <AASegmented<NotifyVia>
          value={via}
          disabled={disabled}
          onChange={(v) => onChange({ notify_via: v })}
          options={[
            {
              value: 'local',
              label: viaLabel('local', perspective),
              icon: BuildingOffice2Icon,
              hint: "The site's own Alert Actions channels send them",
            },
            {
              value: 'cloud',
              label: viaLabel('cloud', perspective),
              icon: CloudIcon,
              hint: "The site hands the alert to the cloud; the cloud's Central Alert Actions send them",
            },
          ]}
        />
        <span className="basis-full text-[11px] text-muted-foreground">
          {via === 'local'
            ? perspective === 'site'
              ? "Uses this site's Email (SMTP) and Message API Server in Alert Actions."
              : "Uses the site's own Alert Actions channels (configured on that site)."
            : "The site relays the alert to the cloud, which sends it with Central Alert Actions' Email (SMTP) and Message API Server."}
          {!sendsOut && ' Only applies once Email or Message API is on.'}
        </span>
      </div>

      {via === 'cloud' && sendsOut && cloudAvailable === false && (
        <Callout tone="warning">
          {cloudReason || 'This site is not connected to a cloud'} — notifications for this rule will fail until it is.
        </Callout>
      )}
    </div>
  );
}
